import { ApiResponse } from "../../../types/api";
import apiClient from "../../apiClient";
import {
  AuditLog,
  AuditLogListResponse,
  GetAuditLogsParams,
  GetTableChangesParams,
  AuditSummaryResponse,
  DataChangeResponse,
} from "./types";

const AUDIT_LOGS_ENDPOINT = "/audit-logs";

export const auditLogService = {
  // Get audit history for a specific record
  getRecordHistory: async (
    tableName: string,
    recordId: string,
    limit: number = 100,
    offset: number = 0,
  ): Promise<AuditLog[]> => {
    const response = await apiClient.get<ApiResponse<AuditLog[]>>(
      `${AUDIT_LOGS_ENDPOINT}/record/${tableName}/${recordId}`,
      {
        params: { limit, offset },
      },
    );
    return response.data.data;
  },

  // Get user activity logs
  getUserActivity: async (
    userId: string,
    startDate?: string,
    endDate?: string,
    limit: number = 100,
    offset: number = 0,
  ): Promise<AuditLog[]> => {
    const params: GetAuditLogsParams = { limit, offset };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get<ApiResponse<AuditLog[]>>(
      `${AUDIT_LOGS_ENDPOINT}/user/${userId}`,
      { params },
    );
    return response.data.data;
  },

  // Get table changes
  getTableChanges: async (
    tableName: string,
    params: GetTableChangesParams = {},
  ): Promise<AuditLog[]> => {
    const response = await apiClient.get<ApiResponse<AuditLog[]>>(
      `${AUDIT_LOGS_ENDPOINT}/table/${tableName}`,
      { params },
    );
    return response.data.data;
  },

  // Get all audit logs with filters
  getAuditLogs: async (
    params: GetAuditLogsParams = {},
  ): Promise<AuditLogListResponse> => {
    const response = await apiClient.get<ApiResponse<AuditLogListResponse>>(
      AUDIT_LOGS_ENDPOINT,
      { params },
    );
    return response.data.data;
  },

  // Get audit summary statistics
  getAuditSummary: async (days: number = 7): Promise<AuditSummaryResponse> => {
    const response = await apiClient.get<ApiResponse<AuditSummaryResponse>>(
      `${AUDIT_LOGS_ENDPOINT}/summary`,
      { params: { days } },
    );
    return response.data.data;
  },

  // Search audit logs
  searchAuditLogs: async (
    searchTerm: string,
    limit: number = 100,
    offset: number = 0,
  ): Promise<AuditLog[]> => {
    const response = await apiClient.get<ApiResponse<AuditLog[]>>(
      `${AUDIT_LOGS_ENDPOINT}/search`,
      {
        params: { q: searchTerm, limit, offset },
      },
    );
    return response.data.data;
  },

  // Get data changes for a record
  getDataChanges: async (
    tableName: string,
    recordId: string,
  ): Promise<DataChangeResponse[]> => {
    const response = await apiClient.get<ApiResponse<DataChangeResponse[]>>(
      `${AUDIT_LOGS_ENDPOINT}/changes/${tableName}/${recordId}`,
    );
    return response.data.data;
  },
};

export type {
  AuditLog,
  AuditLogListResponse,
  GetAuditLogsParams,
  AuditSummaryResponse,
  DataChangeResponse,
};
